import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle, Music } from 'lucide-react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged 
} from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { auth, database } from '../../shared/firebase';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function AuthScreen() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'verify' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const tapsodySnap = await get(ref(database, `bandMembers/${user.uid}`));
        const isTapsodyUser = tapsodySnap.exists();
        
        if (user.emailVerified || isTapsodyUser) {
          navigate('/profiel');
        } else {
          setMode('verify');
        }
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const tapsodySnap = await get(ref(database, `bandMembers/${result.user.uid}`));
      const isTapsodyUser = tapsodySnap.exists();
      
      if (result.user.emailVerified || isTapsodyUser) {
        navigate('/profiel');
      } else {
        setMode('verify');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Onjuist e-mailadres of wachtwoord');
      } else if (err.code === 'auth/invalid-email') {
        setError('Ongeldig e-mailadres');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Te veel pogingen. Probeer het later opnieuw of reset je wachtwoord.');
      } else {
        setError('Er ging iets mis. Probeer het opnieuw.');
      }
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (password.length < 6) {
      setError('Wachtwoord moet minimaal 6 tekens zijn');
      setLoading(false);
      return;
    }

    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(result.user);
      setVerificationSent(true);
      setMode('verify');
    } catch (err) {
      console.error('Register error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('Dit e-mailadres is al in gebruik');
      } else if (err.code === 'auth/invalid-email') {
        setError('Ongeldig e-mailadres');
      } else if (err.code === 'auth/weak-password') {
        setError('Wachtwoord is te zwak');
      } else {
        setError('Er ging iets mis. Probeer het opnieuw.');
      }
    }
    setLoading(false);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResetSent(false);

    if (!email) {
      setError('Vul je e-mailadres in');
      setLoading(false);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      if (err.code === 'auth/user-not-found') {
        setResetSent(true);
      } else if (err.code === 'auth/invalid-email') {
        setError('Ongeldig e-mailadres');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Te veel pogingen. Probeer het later opnieuw.');
      } else {
        setError('Er ging iets mis. Probeer het opnieuw.');
      }
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    if (auth.currentUser) {
      try {
        await sendEmailVerification(auth.currentUser);
        setVerificationSent(true);
      } catch (err) {
        console.error('Resend error:', err);
        setError('Kon verificatie e-mail niet versturen');
      }
    }
  };

  const checkVerification = async () => {
    if (auth.currentUser) {
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        navigate('/profiel');
      } else {
        setError('E-mail is nog niet geverifieerd. Check je inbox.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {mode === 'verify' ? (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#8146FF]/20 flex items-center justify-center">
                <Mail size={32} className="text-[#8146FF]" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Verifieer je e-mail</h2>
              <p className="text-white/60 mb-6">
                We hebben een verificatie link gestuurd naar <strong className="text-white">{auth.currentUser?.email}</strong>
              </p>
              
              {verificationSent && (
                <div className="bg-green-500/10 border border-green-500/20 text-green-400 px-4 py-3 rounded-lg mb-4 flex items-center gap-2 justify-center">
                  <CheckCircle size={18} />
                  <span className="text-sm">Verificatie e-mail verstuurd!</span>
                </div>
              )}
              
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg mb-4 flex items-center gap-2 justify-center">
                  <AlertCircle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <button
                onClick={checkVerification}
                className="w-full bg-[#8146FF] text-white py-3 rounded-full font-semibold hover:bg-[#6a35d9] transition mb-3"
              >
                Ik heb geverifieerd
              </button>
              
              <button
                onClick={handleResendVerification}
                className="text-[#8146FF] text-sm hover:underline"
              >
                Verificatie e-mail opnieuw versturen
              </button>
            </div>
          ) : mode === 'reset' ? (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-2 text-center">Wachtwoord vergeten</h2>
              <p className="text-white/60 text-center mb-6">
                Vul je e-mailadres in en we sturen een reset link.
              </p>

              {resetSent ? (
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle size={32} className="text-green-400" />
                  </div>
                  <p className="text-white/60 mb-6">
                    Als er een account bestaat voor <strong className="text-white">{email}</strong>, ontvang je binnen enkele minuten een e-mail met een reset link.
                  </p>
                  <button
                    onClick={() => { setMode('login'); setResetSent(false); setError(''); }}
                    className="w-full bg-[#8146FF] text-white py-3 rounded-full font-semibold hover:bg-[#6a35d9] transition"
                  >
                    Terug naar inloggen
                  </button>
                </div>
              ) : (
                <>
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
                      <AlertCircle size={18} />
                      <span className="text-sm">{error}</span>
                    </div>
                  )}

                  <form onSubmit={handlePasswordReset}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-white/70 mb-2">E-mailadres</label>
                      <div className="relative">
                        <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full bg-white/10 border border-white/20 rounded-lg pl-12 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#8146FF]"
                          placeholder="jouw@email.nl"
                          required
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#8146FF] text-white py-3 rounded-full font-semibold hover:bg-[#6a35d9] transition disabled:opacity-50"
                    >
                      {loading ? 'Even geduld...' : 'Verstuur reset link'}
                    </button>
                  </form>

                  <div className="mt-6 text-center">
                    <button
                      onClick={() => { setMode('login'); setError(''); }}
                      className="text-[#8146FF] text-sm hover:underline"
                    >
                      Terug naar inloggen
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-2 text-center">
                {mode === 'login' ? 'Welkom terug' : 'Account aanmaken'}
              </h2>
              <p className="text-white/60 text-center mb-6">
                {mode === 'login' 
                  ? 'Log in om verder te gaan' 
                  : 'Vul je gegevens in om te beginnen'
                }
              </p>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
                  <AlertCircle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={mode === 'login' ? handleLogin : handleRegister}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">E-mailadres</label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded-lg pl-12 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#8146FF]"
                        placeholder="jouw@email.nl"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Wachtwoord</label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded-lg pl-12 pr-12 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#8146FF]"
                        placeholder="••••••••"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {mode === 'register' && (
                      <p className="text-xs text-white/40 mt-1">Minimaal 6 tekens</p>
                    )}
                  </div>
                </div>

                {mode === 'login' && (
                  <div className="mt-3 text-right">
                    <button
                      type="button"
                      onClick={() => { setMode('reset'); setError(''); }}
                      className="text-sm text-white/50 hover:text-[#8146FF]"
                    >
                      Wachtwoord vergeten?
                    </button>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#8146FF] text-white py-3 rounded-full font-semibold hover:bg-[#6a35d9] transition mt-6 disabled:opacity-50"
                >
                  {loading 
                    ? 'Even geduld...' 
                    : mode === 'login' ? 'Inloggen' : 'Account aanmaken'
                  }
                </button>
              </form>

              <div className="mt-6 text-center">
                {mode === 'login' ? (
                  <p className="text-white/60 text-sm">
                    Nog geen account?{' '}
                    <button
                      onClick={() => { setMode('register'); setError(''); }}
                      className="text-[#8146FF] font-medium hover:underline"
                    >
                      Registreer hier
                    </button>
                  </p>
                ) : (
                  <p className="text-white/60 text-sm">
                    Al een account?{' '}
                    <button
                      onClick={() => { setMode('login'); setError(''); }}
                      className="text-[#8146FF] font-medium hover:underline"
                    >
                      Log in
                    </button>
                  </p>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-white/10 text-center">
                <p className="text-xs text-white/40">
                  Gebruik je Tapsody? Log in met dezelfde gegevens.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
